import MatchmakingService from '@/services/MatchmakingService';
import marketplaceProductFromResponse from '@/helpers/marketplaceProductResponse';

export default {
  data() {
    return {
      liveProductMessages: [],
      productMessagesPromise: null,
      productMessagesPending: false,
      productPanelSyncing: false,
      pendingProductPanelOpenDetail: null,
    };
  },

  computed: {
    productMsgIds() {
      return (Array.isArray(this.liveProductMessages) ? this.liveProductMessages : [])
        .map((m) => m && m.id)
        .filter((id) => id !== undefined && id !== null);
    },

    counterOfferBulbCount() {
      const offers = Array.isArray(this.mpProductOffers) ? this.mpProductOffers : [];
      return offers.reduce(
        (total, offer) => total
          + this.$store.getters.productOfferBulb(this.mpMessageRole, offer && offer.id),
        0,
      );
    },

    productMessagesCount() {
      const role = this.mpMessageRole;
      const productId = this.mpMessageProductId;
      const total = Number(this.$store.getters.productBulb(role, productId)) || 0;
      const offers = Number(this.counterOfferBulbCount) || 0;
      const residual = Math.max(0, total - offers);
      const loaded = Number(this.$store.getters.productMessageBulb(role, this.productMsgIds)) || 0;
      const live = Number(this.$store.getters.productLiveMessageBulb(role, productId)) || 0;
      return Math.max(residual, loaded, live);
    },
  },

  watch: {
    mpBaseMessages: {
      immediate: true,
      handler(val) {
        this.liveProductMessages = Array.isArray(val) ? [...val] : [];
      },
    },
  },

  mounted() {
    window.addEventListener('phase2-action-panel-open-request', this.onProductPanelOpenRequest);
    window.addEventListener('phase2-action-panel-refresh', this.onProductPanelRefresh);
  },

  beforeDestroy() {
    window.removeEventListener('phase2-action-panel-open-request', this.onProductPanelOpenRequest);
    window.removeEventListener('phase2-action-panel-refresh', this.onProductPanelRefresh);
  },

  methods: {
    onProductPanelOpenRequest(e) {
      const detail = e && e.detail;
      if (!detail || detail.panel !== 'seller') return;
      if (!this.$el || !detail.target || !this.$el.contains(detail.target)) return;
      this.pendingProductPanelOpenDetail = detail;
      this.syncProductMessagesPanel();
    },

    onProductPanelRefresh(e) {
      const detail = e && e.detail;
      if (!detail || detail.panel !== 'seller') return;
      if (!this.$el || !detail.target || !this.$el.contains(detail.target)) return;
      this.syncProductMessagesPanel();
    },

    async syncProductMessagesPanel() {
      if (this.productPanelSyncing) return;
      this.productPanelSyncing = true;
      try {
        const { ok, msgIds } = await this.refreshProductMessages();
        const openDetail = this.pendingProductPanelOpenDetail;
        this.pendingProductPanelOpenDetail = null;
        if (!ok) return;

        if (openDetail) {
          const opened = typeof openDetail.open === 'function' && openDetail.open();
          if (!opened) return;
        }

        await this.$nextTick();
        if (!this.isProductMessagesPanelVisible()) return;

        const panel = this.$refs.productMessagesPanel;
        if (panel && typeof panel.scrollToLastMessage === 'function') {
          await panel.scrollToLastMessage();
        }

        await this.markProductMessagesRead(msgIds);
      } finally {
        this.pendingProductPanelOpenDetail = null;
        this.productPanelSyncing = false;
      }
    },

    isProductMessagesPanelVisible() {
      if (!this.$el || !this.$el.isConnected) return false;
      const panel = this.$refs.productMessagesPanel;
      const el = panel && panel.$el;
      return Boolean(el && el.classList && el.classList.contains('is-visible'));
    },

    async markProductMessagesRead(msgIds) {
      const ids = (Array.isArray(msgIds) ? msgIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0);
      const productId = this.mpMessageProductId;
      if (!productId || !ids.length) return false;
      try {
        await MatchmakingService.markMarketplaceProductMessagesRead(productId, ids);
        return true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return false;
      }
    },

    refreshProductMessages() {
      if (this.productMessagesPromise) {
        this.productMessagesPending = true;
        return this.productMessagesPromise;
      }

      const promise = Promise.resolve().then(() => this.runProductMessagesRefresh());
      this.productMessagesPromise = promise;
      const cleanup = () => {
        if (this.productMessagesPromise === promise) this.productMessagesPromise = null;
      };
      promise.then(cleanup, cleanup);
      return promise;
    },

    async runProductMessagesRefresh() {
      const id = this.mpMessageProductId;
      let result = { ok: false, msgIds: this.productMsgIds };

      if (id) {
        try {
          const { data } = await MatchmakingService.marketplaceProduct(id);
          const product = marketplaceProductFromResponse(data);
          if (product && Array.isArray(product.messages)) {
            this.liveProductMessages = [...product.messages];
          }
          result = { ok: true, msgIds: this.productMsgIds };
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('refreshProductMessages failed', e);
          result = { ok: false, msgIds: this.productMsgIds };
        }
      }

      if (this.productMessagesPending) {
        this.productMessagesPending = false;
        result = await this.runProductMessagesRefresh();
      }

      return result;
    },
  },
};
